import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";

import ElementaryProgressReportDao from "../dao/ElementaryProgressReportDao";

import { buildContentItems } from "../utils/teacher/StudentPageUtils";
import { getGradingCriteriaId, mapStudentRatingSubmission, normalizeRating } from "../utils/teacher/InputRatingUtils";
import { gradeTranslation } from "../utils/SchoolAdmin/translations";
import { getReadingFluencyFromCustomData, mergeReadingFluencyCustomData } from "../const/fluencyCustomData";
import { normalizeComment } from "../utils/ratings/buildCommentPayload";

// ── Module-level constants and pure helpers ────────────────────────────────
// Declared before the hook so they are in scope for all module-level functions.

const K_CONTENT = (id) => `C:${id}`;
const K_DIFF = (grade, contentId) => `D:${gradeTranslation[grade] ?? grade}|${String(contentId)}`;
const isDiffKey = (k) => String(k).startsWith("D:");

// Extracted from saveRatings to reduce cognitive complexity (S3776).
const buildContentMaps = (valuesToUse, commentsByKey, applyExclusion, prevExcludedIds, wantExcludedIds) => {
    const contentRatingsMap = {};
    const contentCommentsMap = {};

    for (const [k, v] of Object.entries(valuesToUse)) {
        if (isDiffKey(k)) continue;
        const id = Number(String(k).slice(2));
        if (applyExclusion && prevExcludedIds.has(id) && v === "EXCLUDED" && !wantExcludedIds.has(id)) {
            contentRatingsMap[id] = null;
            continue;
        }
        contentRatingsMap[id] = v;
    }
    for (const [k, v] of Object.entries(commentsByKey)) {
        if (isDiffKey(k)) continue;
        const id = Number(String(k).slice(2));
        contentCommentsMap[id] = v;
    }
    return { contentRatingsMap, contentCommentsMap };
};

const buildDiffInputs = (valuesByKey, commentsByKey, diffRatings) => {
    const sourceDiff = diffRatings ?? [];
    const byStable = {};
    for (const dr of sourceDiff) byStable[K_DIFF(dr.grade, dr.contentId)] = dr;

    const diffInputs = [];
    for (const [k, rating] of Object.entries(valuesByKey)) {
        if (!isDiffKey(k)) continue;
        const base = byStable[k];
        if (!base) continue;
        diffInputs.push({
            key: base.key ?? null,
            studentDiffId: base.studentDiffId,
            gradeToContentId: base.gradeToContentId,
            contentId: base.contentId,
            contentType: base.contentType,
            rating: rating || null,
            comment: commentsByKey[k] ?? null,
            personalizedLearningPlan: base.personalizedLearningPlan ?? false
        });
    }
    return diffInputs;
};

// Extracted from primeFromExisting. Receives state setters as params so it can
// live outside the hook and avoid adding to the hook's cognitive complexity score.
const applyDiffRatingsToState = (
    diffRatingsPayload,
    nextValues,
    nextComments,
    nextVisible,
    setDiffRatings,
    setDiffGrades,
    previousDiffRatings
) => {
    const prevByContentId = new Map((previousDiffRatings || []).map((d) => [String(d.contentId), d]));
    // Prefer payload grade; keep prior grade only if this row is missing one.
    const coalesced = diffRatingsPayload.map((dr) => ({
        ...dr,
        grade: dr.grade || prevByContentId.get(String(dr.contentId))?.grade || null,
        rating: normalizeRating(dr.rating)
    }));
    for (const dr of coalesced) {
        const kd = K_DIFF(dr.grade, dr.contentId);
        nextValues[kd] = dr.rating || "";
        if (dr.comment) nextComments[kd] = dr.comment;
        if (dr.comment) nextVisible[dr.contentId] = true;
    }
    if (setDiffRatings) setDiffRatings(coalesced);
    if (setDiffGrades) {
        const grades = [...new Set(coalesced.map((c) => c.grade).filter(Boolean))].sort((a, b) =>
            String(a).localeCompare(String(b))
        );
        setDiffGrades(grades);
    }
};

const matchGle = (gle, std, grade, contentId) => {
    if (String(gle.key) !== String(contentId) && String(gle.gradeToContentKey) !== String(contentId)) return null;
    return {
        templateContentId: `diff-${grade}-gle-${gle.key}`,
        contentType: "GLE",
        label: gle.gleName,
        standardName: std.standardName,
        gleName: gle.gleName,
        eoName: null,
        keyForUI: K_DIFF(grade, gle.key),
        __diffMeta: { grade, contentId: gle.key, match: "GLE" }
    };
};

const matchEo = (eo, gle, std, grade, contentId) => {
    if (String(eo.key) !== String(contentId)) return null;
    return {
        templateContentId: `diff-${grade}-${eo.key}`,
        contentType: "EVIDENCE_OUTCOME",
        label: eo.body,
        standardName: std.standardName,
        gleName: gle.gleName,
        eoName: null,
        keyForUI: K_DIFF(grade, eo.key),
        __diffMeta: { grade, contentId: eo.key, match: "EO" }
    };
};

const matchEoChild = (child, eo, gle, std, grade, contentId) => {
    if (String(child.key) !== String(contentId)) return null;
    return {
        templateContentId: `diff-${grade}-child-${child.key}`,
        contentType: "EVIDENCE_OUTCOME_CHILD",
        label: child.body,
        standardName: std.standardName,
        gleName: gle.gleName,
        eoName: eo.body,
        keyForUI: K_DIFF(grade, child.key),
        __diffMeta: { grade, contentId: child.key, parentId: eo.key, match: "EO_CHILD" }
    };
};

const findInStandard = (std, grade, contentId) => {
    for (const gle of std.gles || []) {
        const gleMatch = matchGle(gle, std, grade, contentId);
        if (gleMatch) return gleMatch;

        for (const eo of gle.evidenceOutcomes || []) {
            const eoMatch = matchEo(eo, gle, std, grade, contentId);
            if (eoMatch) return eoMatch;

            for (const child of eo.evidenceOutcomeChildList || []) {
                const childMatch = matchEoChild(child, eo, gle, std, grade, contentId);
                if (childMatch) return childMatch;
            }
        }
    }
    return null;
};

const makeDiffItem = ({ grade, contentId }, content) => {
    if (!content || !Array.isArray(content.gradeLevels)) return null;
    const gradeNode = content.gradeLevels.find((gl) => gl.gradeLevel === grade);
    if (!gradeNode) return null;

    for (const std of gradeNode.cdeStandards || []) {
        const match = findInStandard(std, grade, contentId);
        if (match) return match;
    }
    return null;
};

/**
 * Hook that orchestrates:
 * - Deriving the correct grading criteria and student IDs
 * - Priming UI from group navigation (GROUPS view) and/or server
 * - Managing controlled state for ratings and comments
 * - Persisting subject/overall comments and ratings
 *
 */
const useStudentRatings = ({
    student,
    studentProfileData,
    selectedEpr,
    selectedTemplate,
    token,
    userGuid,
    view,
    gradingCriteriaId,
    reportableContent = null,
    diffRatings,
    setDiffRatings,
    setDiffGrades
}) => {
    // --- State
    const [valuesByKey, setValuesByKey] = useState({});
    const [commentsByKey, setCommentsByKey] = useState({});

    const [commentVisible, setCommentVisible] = useState({});
    const [hasChanges, setHasChanges] = useState(false);
    const [refetchTrigger, setRefetchTrigger] = useState(0);

    const [existingRatingsData, setExistingRatingsData] = useState([]);
    const [studentOverallComment, setStudentOverallComment] = useState("");
    const [studentSubjectComment, setStudentSubjectComment] = useState("");
    const [studentSubjectCommentKey, setStudentSubjectCommentKey] = useState(null);
    const [studentReadingFluency, setStudentReadingFluencyState] = useState("");
    const [studentSubjectCustomData, setStudentSubjectCustomData] = useState(null);
    /** Session map so parent preview can show WPM for subjects visited this session. */
    const [fluencyByCriteriaId, setFluencyByCriteriaId] = useState({});
    /**
     * Subject-name map for preview inject. Criteria→subject reverse lookup is brittle
     * (missing gradingCriteriaList / template mismatch); this is the durable source of truth.
     */
    const [fluencyBySubjectName, setFluencyBySubjectName] = useState({});

    // --- Stable fallback to keep deps calm

    const contentItems = useMemo(() => buildContentItems(selectedTemplate), [selectedTemplate]);

    /**
     * Resolve the grading criteria id used to fetch and save ratings.
     * - GROUPS: use passed gradingCriteriaId always
     * - HOMEROOM: compute from EPR/template
     * @type {number|string|null}
     */
    const effectiveCriteriaId = useMemo(() => {
        if (gradingCriteriaId) return gradingCriteriaId;
        if (view === "GROUPS") return null;
        if (selectedEpr && selectedTemplate) {
            return getGradingCriteriaId(student, null, selectedEpr, selectedTemplate, "HOMEROOM");
        }
        return null;
    }, [gradingCriteriaId, view, selectedEpr, selectedTemplate, student]);

    const rememberFluency = useCallback((wpm, criteriaId, subjectName, { clearSubject = true } = {}) => {
        const next = wpm ?? "";
        if (criteriaId != null && criteriaId !== "") {
            setFluencyByCriteriaId((prev) => ({ ...prev, [String(criteriaId)]: next }));
        }
        if (!subjectName) return;
        // Empty fetches during a subject-tab switch can still see the previous
        // selectedTemplate.subjectName — never let that wipe another subject's WPM.
        if (!String(next).trim() && !clearSubject) return;
        setFluencyBySubjectName((prev) => {
            if (!String(next).trim()) {
                const nextMap = { ...prev };
                delete nextMap[subjectName];
                return nextMap;
            }
            return { ...prev, [subjectName]: next };
        });
    }, []);

    const setStudentReadingFluency = useCallback(
        (raw) => {
            const next = raw ?? "";
            setStudentReadingFluencyState(next);
            rememberFluency(next, effectiveCriteriaId, selectedTemplate?.subjectName, { clearSubject: true });
        },
        [effectiveCriteriaId, rememberFluency, selectedTemplate?.subjectName]
    );

    const effectiveStudentId = student?.gradingProfileStudentId ?? null;
    // Always mirrors the student currently on screen — used to ignore stale save responses.
    const displayedStudentIdRef = useRef(effectiveStudentId);
    displayedStudentIdRef.current = effectiveStudentId;

    // --- Hydrate overall comment from studentProfileData when available.
    // Guard: only apply the comment when the profile data belongs to the currently
    // displayed student. Without this check, a slow/stale fetch resolving after a
    // fast student switch would write the wrong student's comment into the UI.
    useEffect(() => {
        if (!studentProfileData) return;
        if (String(studentProfileData.gradingProfileStudentId) !== String(effectiveStudentId)) return;

        const profileComment = studentProfileData.gradingProfileStudentComment ?? "";
        setStudentOverallComment(profileComment || "");
    }, [studentProfileData, effectiveStudentId]);

    // primeDiffRatings: thin wrapper so primeFromExisting can call the module-level
    // applyDiffRatingsToState without holding a useCallback closure with branches.
    const primeDiffRatings = (diffRatingsPayload, nextValues, nextComments, nextVisible) =>
        applyDiffRatingsToState(
            diffRatingsPayload,
            nextValues,
            nextComments,
            nextVisible,
            setDiffRatings,
            setDiffGrades,
            diffRatings
        );

    const primeFromExisting = (payload) => {
        const nextValues = {};
        const nextComments = {};
        const nextVisible = {};

        for (const r of payload?.contentRatings || []) {
            const k = K_CONTENT(r.templateContentId);
            nextValues[k] = normalizeRating(r.rating);
            if (r.comment) nextComments[k] = r.comment;
            if (r.comment) nextVisible[r.templateContentId] = true;
        }

        if (Array.isArray(payload?.diffRatings)) {
            primeDiffRatings(payload.diffRatings, nextValues, nextComments, nextVisible);
        }

        const existingStudentSubject = payload?.contentStudentComments || null;
        setStudentSubjectComment(existingStudentSubject?.comment || "");
        setStudentSubjectCommentKey(existingStudentSubject?.key ?? null);
        setStudentSubjectCustomData(existingStudentSubject?.customData ?? null);
        const wpm = getReadingFluencyFromCustomData(existingStudentSubject?.customData);
        setStudentReadingFluencyState(wpm);
        const criteriaForFluency = existingStudentSubject?.gradingCriteria ?? effectiveCriteriaId;
        // clearSubject: false — tab switches update criteriaId before selectedTemplate;
        // an empty Math prime must not erase a still-labeled "ELA" session value.
        rememberFluency(wpm, criteriaForFluency, selectedTemplate?.subjectName, { clearSubject: false });

        setStudentOverallComment(payload?.comment || "");
        setValuesByKey(nextValues);
        setCommentsByKey(nextComments);
        setCommentVisible(nextVisible);
    };

    // Clear the overall comment whenever the student changes, regardless of which tab is active.
    // Without this, the SLE tab (where selectedTemplate is null) never triggers the main
    // clearing effect below, leaving the previous student's overall comment visible.
    const lastStudentIdRef = useRef(null);
    useEffect(() => {
        if (!effectiveStudentId) return;
        if (effectiveStudentId !== lastStudentIdRef.current) {
            lastStudentIdRef.current = effectiveStudentId;
            setStudentOverallComment("");
        }
    }, [effectiveStudentId]);

    const lastFetchKeyRef = useRef(null);
    /** Clear session fluency only when student/view changes — not on subject-tab switches. */
    const lastFluencyScopeRef = useRef(null);

    // Extracted from the fetch useEffect to reduce cognitive complexity.
    const fetchAndPrimeRatings = async (currentKey) => {
        try {
            const res = await ElementaryProgressReportDao({
                action: "getStudentRatings",
                gradingProfileStudentId: effectiveStudentId,
                gradingCriteriaId: effectiveCriteriaId,
                token
            });
            if (lastFetchKeyRef.current !== currentKey) return;
            const payload = res?.data?.payload || null;
            if (payload) {
                setExistingRatingsData([payload]);
                primeFromExisting(payload);
            } else {
                setExistingRatingsData([]);
            }
        } catch (e) {
            if (lastFetchKeyRef.current !== currentKey) return;
            console.error(e);
            toast.error("There was a problem loading existing ratings.");
        }
    };

    // Single effect to clear → maybe pre-prime (GROUPS) → fetch server truth.
    useEffect(() => {
        if (!student) return;
        const nextKey =
            selectedTemplate && effectiveCriteriaId && effectiveStudentId && token
                ? `${view}|${effectiveStudentId}|${effectiveCriteriaId}`
                : null;

        const fluencyScope = effectiveStudentId && token ? `${view}|${effectiveStudentId}` : null;
        if (fluencyScope && fluencyScope !== lastFluencyScopeRef.current) {
            lastFluencyScopeRef.current = fluencyScope;
            setFluencyByCriteriaId({});
            setFluencyBySubjectName({});
        }

        const identityChanged = nextKey && nextKey !== lastFetchKeyRef.current;

        if (identityChanged) {
            setCommentVisible({});
            setExistingRatingsData([]);
            setStudentSubjectComment("");
            setStudentSubjectCommentKey(null);
            setStudentSubjectCustomData(null);
            setStudentReadingFluencyState("");
            setValuesByKey({});
            setCommentsByKey({});
            setHasChanges(false);
        }

        if (!nextKey) return;

        lastFetchKeyRef.current = nextKey;
        fetchAndPrimeRatings(nextKey);
    }, [student, selectedTemplate, token, view, effectiveCriteriaId, effectiveStudentId, refetchTrigger]); // eslint-disable-line react-hooks/exhaustive-deps

    // Keep grade order stable (K, 1st, 2nd, …)

    const diffItemsByGrade = useMemo(() => {
        if (!reportableContent || !Array.isArray(diffRatings) || diffRatings.length === 0) {
            return null;
        }
        const gradeOrder = ["K", "FIRST", "SECOND", "THIRD", "FOURTH", "FIFTH", "SIXTH", "SEVENTH", "EIGHTH"];
        const gradeIndex = (g) => {
            const i = gradeOrder.indexOf(g);
            return i === -1 ? 999 : i;
        };

        const makeDiffItem_local = (dr) => makeDiffItem(dr, reportableContent);
        const grouped = {};
        for (const dr of diffRatings) {
            const item = makeDiffItem_local(dr);
            if (!item) continue;
            const g = dr.grade;
            if (!grouped[g]) grouped[g] = [];
            grouped[g].push(item);
        }

        return Object.fromEntries(
            Object.entries(grouped)
                .sort(([ga], [gb]) => gradeIndex(ga) - gradeIndex(gb))
                .map(([g, items]) => [g, items])
        );
    }, [diffRatings, reportableContent]);

    // --- Derived counters from last loaded server payload
    const totalContentItems = contentItems.length;
    const totalContentRatings = existingRatingsData[0]?.contentRatings?.length ?? 0;
    const totalDiffRatings = existingRatingsData[0]?.diffRatings?.length ?? 0;
    const totalDiffRatingsRated =
        existingRatingsData[0]?.diffRatings?.filter((dr) => dr.rating !== "UNRATED").length ?? 0;
    const totalContentComments = existingRatingsData[0]?.contentRatings?.filter((cr) => cr.comment)?.length ?? 0;
    const totalDiffComments = existingRatingsData[0]?.diffRatings?.filter((dr) => dr.comment)?.length ?? 0;
    const totalRatingsCombined = totalContentRatings + totalDiffRatingsRated;
    const totalCommentsCombined = totalContentComments + totalDiffComments;
    /**
     * Toggle comment visibility for a given content row.
     * @param {string|number} templateContentId
     */
    const toggleCommentVisible = (templateContentId) => {
        setCommentVisible((prev) => ({ ...prev, [templateContentId]: !prev[templateContentId] }));
    };

    const onChangeRatingByKey = (key, value) => {
        setValuesByKey((m) => ({ ...m, [key]: value || null }));
        setHasChanges(true);
    };

    const onChangeCommentByKey = (key, text) => {
        setCommentsByKey((m) => ({ ...m, [key]: text ?? "" }));
        setHasChanges(true);
    };

    /**
     * Persist the student's subject-specific comment for the active grading criteria.
     * Uses `updateStudentCriteriaComments`, then re-primes state from the server response/fallback fetch.
     * Safe to call multiple times.
     * @returns {Promise<void>}
     */
    // Extracted from saveStudentSubjectComment to reduce cognitive complexity.
    const handleSubjectCommentResponse = useCallback(
        async (updated, savedForStudentId) => {
            // Student switcher: ignore responses that no longer match the on-screen student.
            if (String(displayedStudentIdRef.current) !== String(savedForStudentId)) return;

            if (!Array.isArray(updated)) return;
            const updatedRow = updated.find(
                (c) =>
                    Number(c.gradingCriteria) === Number(effectiveCriteriaId) &&
                    String(c.gradingProfileStudentId) === String(savedForStudentId)
            );
            if (updatedRow) {
                setStudentSubjectComment(updatedRow.comment || "");
                setStudentSubjectCommentKey(updatedRow.key ?? null);
                setStudentSubjectCustomData(updatedRow.customData ?? null);
                const wpm = getReadingFluencyFromCustomData(updatedRow.customData);
                setStudentReadingFluencyState(wpm);
                const criteriaForFluency = updatedRow.gradingCriteria ?? effectiveCriteriaId;
                rememberFluency(wpm, criteriaForFluency, selectedTemplate?.subjectName, { clearSubject: true });
                return;
            }
            const fresh = await ElementaryProgressReportDao({
                action: "getStudentRatings",
                gradingProfileStudentId: savedForStudentId,
                gradingCriteriaId: effectiveCriteriaId,
                token
            });
            if (String(displayedStudentIdRef.current) !== String(savedForStudentId)) return;
            const payload = fresh?.data?.payload || null;
            if (payload) {
                setExistingRatingsData([payload]);
                primeFromExisting(payload);
            }
        },
        [effectiveCriteriaId, rememberFluency, selectedTemplate?.subjectName, token] // eslint-disable-line react-hooks/exhaustive-deps
    );

    const saveStudentSubjectComment = useCallback(async () => {
        if (!student || !selectedTemplate || !token || !effectiveCriteriaId || !effectiveStudentId) return;

        const savedForStudentId = effectiveStudentId;
        const comment = normalizeComment(studentSubjectComment);
        const fluency = String(studentReadingFluency ?? "").trim();
        const existingFluency = getReadingFluencyFromCustomData(studentSubjectCustomData);
        const bodyRow = {
            key: studentSubjectCommentKey,
            gradingProfileStudentId: savedForStudentId,
            gradingCriteria: Number(effectiveCriteriaId),
            comment: comment ?? "",
            lastUpdaterGuid: userGuid
        };
        // Always send customData when fluency is set or was previously set (clear → {}).
        const customData = mergeReadingFluencyCustomData(studentSubjectCustomData, fluency);
        if (customData) bodyRow.customData = customData;
        else if (existingFluency) bodyRow.customData = {};

        // Allow create with null comment + fluency only
        if (studentSubjectCommentKey == null && !comment && !fluency) return;

        const body = [
            {
                ...bodyRow,
                comment: comment
            }
        ];

        try {
            const res = await ElementaryProgressReportDao({
                action: "updateStudentCriteriaComments",
                gradingCriteriaId: effectiveCriteriaId,
                data: body,
                token
            });

            const updated = res?.data?.payload || null;
            await handleSubjectCommentResponse(updated, savedForStudentId);
            if (String(displayedStudentIdRef.current) === String(savedForStudentId)) {
                toast.success("Comment successfully saved.");
            }
        } catch (e) {
            console.error(e);
            if (String(displayedStudentIdRef.current) === String(savedForStudentId)) {
                toast.error("Comment was not saved. Please make note of your comment and try again later.");
            }
        }
    }, [
        student,
        selectedTemplate,
        token,
        effectiveCriteriaId,
        effectiveStudentId,
        studentSubjectComment,
        studentSubjectCommentKey,
        studentReadingFluency,
        studentSubjectCustomData,
        handleSubjectCommentResponse,
        userGuid
    ]);

    /**
     * Persist the student's overall (profile-level) comment for the active EPR.
     * Uses `updateStudent`.
     * @returns {Promise<void>}
     */
    const saveStudentOverallComment = useCallback(async () => {
        if (!student || !selectedEpr || !token) return;
        const normalizeStudentKey = (key) => (key == null ? key : String(key).trim().split("-").pop());
        const savedForStudentId = student?.gradingProfileStudentId ?? normalizeStudentKey(student.key);
        const body = [
            {
                key: normalizeStudentKey(student.key),
                gradingProfile: student.gradingProfile || studentProfileData?.gradingProfileId,
                studentNumber: student.studentNumber,
                firstName: student.firstName,
                lastName: student.lastName,
                sectionId: student.sectionId || studentProfileData?.sectionId,
                comment: studentOverallComment || ""
            }
        ];

        try {
            await ElementaryProgressReportDao({
                action: "updateStudent",
                gradingProfileId: selectedEpr.key,
                data: body,
                token
            });
            if (String(displayedStudentIdRef.current) === String(savedForStudentId)) {
                toast.success("Comment successfully saved.");
            }
        } catch (e) {
            console.error(e);
            if (String(displayedStudentIdRef.current) === String(savedForStudentId)) {
                toast.error("Comment was not saved. Please make note of your comment and try again later.");
            }
        }
    }, [student, studentProfileData, selectedEpr, studentOverallComment, token]);

    /**
     * Persist all item ratings/comments for this student  gradingCriteria.
     * Uses `updateStudentRatings`, then re-primes UI from the server response.
     * @returns {Promise<void>}
     */
    const saveRatings = useCallback(
        async (overrides = null, applyExclusion = false) => {
            if (!student || !selectedTemplate || !token || !effectiveCriteriaId || !effectiveStudentId) return;
            const savedForStudentId = effectiveStudentId;
            try {
                const valuesToUse = overrides ? { ...valuesByKey, ...overrides } : valuesByKey;
                const wantExcludedIds = new Set(
                    Object.entries(overrides || {})
                        .filter(([k, v]) => !isDiffKey(k) && v === "EXCLUDED")
                        .map(([k]) => Number(String(k).slice(2)))
                );
                const prevExcludedIds = new Set(
                    (existingRatingsData?.[0]?.contentRatings || [])
                        .filter((r) => r.rating === "EXCLUDED")
                        .map((r) => r.templateContentId)
                );

                const { contentRatingsMap, contentCommentsMap } = buildContentMaps(
                    valuesToUse,
                    commentsByKey,
                    applyExclusion,
                    prevExcludedIds,
                    wantExcludedIds
                );
                const diffInputs = buildDiffInputs(valuesByKey, commentsByKey, diffRatings);

                const payload = mapStudentRatingSubmission({
                    student,
                    selectedEpr,
                    selectedTemplate,
                    ratings: contentRatingsMap,
                    comments: contentCommentsMap,
                    contentItems,
                    existingRatingsData,
                    diffInputs,
                    userGuid,
                    providedGradingCriteriaId: effectiveCriteriaId
                });

                const res = await ElementaryProgressReportDao({
                    action: "updateStudentRatings",
                    gradingCriteriaId: effectiveCriteriaId,
                    gradingProfileStudentId: savedForStudentId,
                    data: payload,
                    token
                });

                // Student switcher: never apply a stale save onto the newly displayed student.
                if (String(displayedStudentIdRef.current) !== String(savedForStudentId)) return;

                const updated = res?.data?.payload || null;
                if (updated) {
                    setExistingRatingsData([updated]);
                    primeFromExisting(updated);
                    if (Array.isArray(updated.diffRatings)) setDiffRatings?.(updated.diffRatings);
                    setHasChanges(false);
                    toast.success("Ratings saved.", { autoClose: 3000 });
                }
            } catch (error) {
                console.error("Failed to save ratings:", error);
                if (String(displayedStudentIdRef.current) === String(savedForStudentId)) {
                    toast.error("Changes were not saved. Please make note of your changes and try again.", {
                        autoClose: 5000
                    });
                }
            }
        },
        // primeFromExisting is a plain fn recreated each render — listing it would
        // cause saveRatings to recreate on every render too.
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [
            student,
            selectedTemplate,
            token,
            effectiveCriteriaId,
            effectiveStudentId,
            valuesByKey,
            commentsByKey,
            contentItems,
            existingRatingsData,
            selectedEpr,
            userGuid,
            setDiffRatings
        ] // eslint-disable-line react-hooks/exhaustive-deps -- primeFromExisting is a plain fn, adding it would re-create saveRatings every render
    );

    return {
        // data
        contentItems,
        totalContentItems,
        totalDiffRatings,
        totalRatingsCombined,
        totalCommentsCombined,
        diffItemsByGrade,
        valuesByKey,
        commentsByKey,

        // state
        existingRatingsData,
        ratings: {},
        comments: {},
        commentVisible,
        studentOverallComment,
        setStudentOverallComment,
        studentSubjectComment,
        setStudentSubjectComment,
        studentReadingFluency,
        setStudentReadingFluency,
        fluencyByCriteriaId,
        fluencyBySubjectName,
        hasChanges,

        // actions
        toggleCommentVisible,
        onChangeRatingByKey,
        onChangeCommentByKey,
        saveStudentSubjectComment,
        saveStudentOverallComment,
        saveRatings,
        // Force a re-fetch of getStudentRatings (which carries grade-populated diffRatings)
        refetchRatings: () => {
            lastFetchKeyRef.current = null;
            setRefetchTrigger((n) => n + 1);
        }
    };
};

export default useStudentRatings;
